import React, { useEffect, useState } from 'react';
import { Sponsor } from '../types/global';
import { Surface } from '../config/sponsorTiers';
import { getSponsorsForSurface } from '../services/publicSponsors';
import './PartnerSlot.css';

interface PartnerSlotProps {
  surface: Surface;
  label?: string;
  className?: string;
}

/**
 * The governed "small spaces" partner slot (Build Spec §3.1). Renders only the
 * sponsors admins have placed on this surface — active, one per brand, tier
 * eligible, enabled in Placement Control, in admin order, capped by maxSlots.
 * Renders nothing until the admin policy AND sponsors have both loaded (so a
 * tier the admin switched off never flashes in under the default policy), and
 * nothing when none qualify.
 */
const PartnerSlot: React.FC<PartnerSlotProps> = ({ surface, label = 'Presented by our partners', className }) => {
  const [selected, setSelected] = useState<Sponsor[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getSponsorsForSurface(surface);
        if (!cancelled) setSelected(list);
      } catch {
        /* no partner slot on error */
      }
    })();
    return () => { cancelled = true; };
  }, [surface]);

  if (selected.length === 0) return null;

  return (
    <div className={`partner-slot ${className || ''}`}>
      <span className="partner-slot-label">{label}</span>
      <div className="partner-slot-logos">
        {selected.map(s => (
          <a key={s.id} href={s.website || undefined} target="_blank" rel="noopener noreferrer" title={s.name} aria-label={s.name}>
            {s.logo ? <img src={s.logo} alt={s.name} loading="lazy" /> : <span className="partner-slot-name">{s.name}</span>}
          </a>
        ))}
      </div>
    </div>
  );
};

export default PartnerSlot;
